import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const { branding: globalBranding } = useBranding();
  const { tenant } = useTenant();
  // Tenant branding takes priority over global branding when on a subdomain
  const branding = tenant?.branding
    ? {
        ...globalBranding,
        logo_url: tenant.branding.logo_url || globalBranding.logo_url,
        accent_color: tenant.branding.accent_color || globalBranding.accent_color,
        primary_color: tenant.branding.accent_color || globalBranding.primary_color,
        app_name: tenant.branding.brand_name || globalBranding.app_name,
        login_title: tenant.branding.brand_name ? `Bienvenido a ${tenant.branding.brand_name}` : globalBranding.login_title,
      }
    : globalBranding;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }
    if (plan) navigate(`/dashboard/plans?plan=${encodeURIComponent(plan)}`, { replace: true });
    setLoading(false);
  };

  const pc = branding.primary_color || "#0f172a";
  const ac = branding.accent_color || "#22c55e";

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: branding.secondary_color || "#f1f5f9" }} data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: pc }}>
                <MessageSquare className="w-6 h-6" style={{ color: ac }} />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">{branding.login_title || "Welcome back"}</CardTitle>
          <CardDescription>{branding.login_subtitle || "Sign in to your WhatsApp Bot Dashboard"}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200" data-testid="login-error">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:underline" data-testid="forgot-password-link">¿Olvidaste tu contraseña?</Link>
              </div>
              <Input id="password" type="password" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" style={{ backgroundColor: pc }} disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to={plan ? `/register?plan=${encodeURIComponent(plan)}` : "/register"} className="font-medium hover:underline" style={{ color: pc }} data-testid="register-link">Register</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
